import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Perutean.
 *
 * Ditulis sendiri, bukan memakai pustaka: aplikasinya punya delapan rute dan
 * kebutuhannya sederhana. React Router menambah sekitar 12 kB terkompresi ke
 * bundel yang justru paling terasa di halaman share link — permukaan yang
 * paling sering dibuka dari ponsel dengan data seluler.
 *
 * Yang tetap harus benar dan sering salah pada router buatan sendiri:
 * tombol Kembali peramban, penggantian riwayat, posisi gulir, dan tautan
 * yang dibuka di tab baru dengan Ctrl/Cmd-klik.
 */

interface Lokasi {
  path: string
  search: URLSearchParams
}

interface RouterValue extends Lokasi {
  navigate: (to: string, opsi?: { replace?: boolean; keepScroll?: boolean }) => void
  /** Perbarui query tanpa menambah entri riwayat baru. */
  setQuery: (ubah: Record<string, string | null>, opsi?: { replace?: boolean }) => void
}

const Ctx = createContext<RouterValue | null>(null)

function bacaLokasi(): Lokasi {
  return {
    path: window.location.pathname,
    search: new URLSearchParams(window.location.search),
  }
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [lokasi, setLokasi] = useState<Lokasi>(bacaLokasi)

  useEffect(() => {
    const onPop = () => setLokasi(bacaLokasi())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = useCallback<RouterValue['navigate']>((to, opsi = {}) => {
    const url = new URL(to, window.location.origin)
    // Hanya navigasi internal yang ditangani di sini; tautan keluar
    // dibiarkan diproses peramban seperti biasa.
    if (url.origin !== window.location.origin) {
      window.location.href = to
      return
    }

    const tujuan = url.pathname + url.search
    const sekarang = window.location.pathname + window.location.search
    if (tujuan === sekarang) return

    if (opsi.replace) window.history.replaceState(null, '', tujuan)
    else window.history.pushState(null, '', tujuan)

    setLokasi(bacaLokasi())
    if (!opsi.keepScroll) window.scrollTo(0, 0)
  }, [])

  const setQuery = useCallback<RouterValue['setQuery']>((ubah, opsi = {}) => {
    const sp = new URLSearchParams(window.location.search)
    for (const [k, v] of Object.entries(ubah)) {
      if (v === null || v === '') sp.delete(k)
      else sp.set(k, v)
    }
    const s = sp.toString()
    const tujuan = window.location.pathname + (s ? `?${s}` : '')

    // replace sebagai bawaan: mengubah penyaring atau urutan tidak seharusnya
    // memenuhi riwayat sehingga tombol Kembali harus ditekan belasan kali.
    if (opsi.replace === false) window.history.pushState(null, '', tujuan)
    else window.history.replaceState(null, '', tujuan)

    setLokasi(bacaLokasi())
  }, [])

  const value = useMemo<RouterValue>(
    () => ({ ...lokasi, navigate, setQuery }),
    [lokasi, navigate, setQuery],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useRouter(): RouterValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useRouter dipakai di luar RouterProvider')
  return v
}

/**
 * Tautan internal.
 *
 * Ctrl/Cmd/Shift-klik dan klik tengah sengaja dibiarkan lolos ke peramban,
 * supaya "buka di tab baru" tetap berfungsi — perilaku yang sering hilang
 * pada router buatan sendiri dan langsung terasa mengganggu.
 */
export function Link({
  to,
  replace,
  children,
  onClick,
  ...rest
}: {
  to: string
  replace?: boolean
  children: ReactNode
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>) {
  const { navigate } = useRouter()

  return (
    <a
      href={to}
      onClick={(e) => {
        onClick?.(e)
        if (e.defaultPrevented) return
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        e.preventDefault()
        navigate(to, replace ? { replace: true } : {})
      }}
      {...rest}
    >
      {children}
    </a>
  )
}

/**
 * Cocokkan path terhadap pola sederhana dengan segmen `:nama`.
 * Mengembalikan parameter bila cocok, atau null.
 */
export function cocok(pola: string, path: string): Record<string, string> | null {
  const segPola = pola.split('/').filter(Boolean)
  const segPath = path.split('/').filter(Boolean)

  // Pola berakhiran '*' menerima sisa path berapa pun panjangnya.
  const wildcard = segPola[segPola.length - 1] === '*'
  if (wildcard) segPola.pop()
  else if (segPola.length !== segPath.length) return null

  if (wildcard && segPath.length < segPola.length) return null

  const params: Record<string, string> = {}
  for (let i = 0; i < segPola.length; i++) {
    const p = segPola[i] as string
    const v = segPath[i] as string
    if (p.startsWith(':')) {
      params[p.slice(1)] = decodeURIComponent(v)
    } else if (p !== v) {
      return null
    }
  }

  if (wildcard) params['*'] = segPath.slice(segPola.length).map(decodeURIComponent).join('/')
  return params
}
